// First-run hints: who gets them, and when in the start phase each one shows.
//
// A new player on a phone does not know the screen's lower halves steer, and
// on a computer does not know which keys are theirs or which worm they are.
// So while they are new (a guest, or an account under HINT_GAMES games, and
// under HINT_GAMES matches started on this device) the FIRST round of the
// match walks them through it. One round is enough to learn the controls, and
// from round two on the board looks like everyone else's (name tags back, no
// key cards). The count is kept here in localStorage; the account count comes
// from the cached rating row (the same one the lobby's rank badge seeds from),
// so a veteran on a fresh device is spared after one fetch.
import { useEffect, useState, useSyncExternalStore } from "react";
import requestFrame from "./request-frame.js";
import { cachedRating } from "./rating/store";

export const HINT_GAMES = 4;
const KEY = "ar.matchesStarted";
// Flip to true to show the hints to everyone, every round, while tuning them
// (?hints=all does it for one browser session without a build).
const HINTS_FOR_EVERYONE = false;

function readCount(): number {
  try {
    return Number(localStorage.getItem(KEY) || 0) || 0;
  } catch {
    return 0;
  }
}

const listeners = new Set<() => void>();
export function noteMatchStarted(): void {
  try {
    localStorage.setItem(KEY, String(readCount() + 1));
  } catch {
    // storage blocked: hints stay for this device, no harm done
  }
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

// True while this player should still see the hints. `agentId` is the signed-in
// account (null for a guest); `roundNumber` is the match's 1-based round (0 for
// a match that has not started a round yet), since only its first round is
// walked through.
export function useFirstRun(agentId: string | null | undefined, roundNumber: number = 1): boolean {
  const started = useSyncExternalStore(subscribe, readCount, () => 0);
  // ?hints=1 forces them on as a new player sees them (first round only);
  // ?hints=all on for every round, for tuning them; ?hints=0 off. Remembered
  // for the session, since the game route drops the query.
  const forced = readForced();
  const firstRound = roundNumber <= 1;
  if (forced === "all") return true;
  if (forced === "1") return firstRound;
  if (forced === "0") return false;
  if (HINTS_FOR_EVERYONE) return true;
  if (!firstRound) return false;
  if (started >= HINT_GAMES) return false;
  if (agentId) {
    const row = cachedRating(agentId);
    if (row && row.games >= HINT_GAMES) return false;
  }
  return true;
}

function readForced(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const q = new URLSearchParams(window.location.search).get("hints");
    if (q === "1" || q === "0" || q === "all") sessionStorage.setItem("ar.hints", q);
    return sessionStorage.getItem("ar.hints");
  } catch {
    return null;
  }
}

// The hint phases through the start phase (roll call + countdown), in seconds
// on the round's render clock. Touch walks left, then right, through the roll
// call, and "you" takes over as the roll call's name tags fade (the label
// lands where your tag was) and holds through the countdown; keys show the
// turn keys around your head for the whole run.
export type HintPhase = "left" | "right" | "you" | "keys" | null;
export function hintPhaseAt(t: number, startPhaseTime: number, rollCallTime: number, touch: boolean): HintPhase {
  const lead = 0.4;
  if (t < lead || t >= startPhaseTime) return null;
  if (!touch) return "keys";
  // no roll call (the shop's try-out): the first half of the phase is the walk
  const youAt = rollCallTime > 0 ? Math.min(rollCallTime, startPhaseTime) : startPhaseTime * 0.55;
  const beat = Math.max(0.6, (youAt - lead) / 2);
  if (t < youAt && t < lead + beat) return "left";
  if (t < youAt) return "right";
  return "you";
}

// The current phase for a round, tracked on its render clock; re-renders the
// caller only when the phase changes (a handful of times per round). Off
// (null, no loop) when `enabled` is false.
export function useHintPhase(enabled: boolean, gameState: { startPhaseTime: number; rollCallTime?: number } | null | undefined, getRenderTime: () => number, touch: boolean): HintPhase {
  const [phase, setPhase] = useState<HintPhase>(null);
  useEffect(() => {
    if (!enabled || !gameState) {
      setPhase(null);
      return;
    }
    let id = 0;
    const loop = () => {
      const t = getRenderTime();
      const p = hintPhaseAt(t, gameState.startPhaseTime, gameState.rollCallTime || 0, touch);
      setPhase((prev) => (prev === p ? prev : p));
      if (t < gameState.startPhaseTime) id = requestFrame(loop);
    };
    id = requestFrame(loop);
    return () => window.cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, gameState, touch]);
  return phase;
}
